import React, { useState } from "react";
import { navigate } from "@reach/router";
import { Formik } from "formik";
import * as XLSX from "xlsx";
import styled from "styled-components";

import {
  CustomFileUpload,
  CustomInputField,
  CustomCheckbox,
  CheckBoxLabel,
  CheckboxContainer,
} from "../../../common/FormInputs";
import { Autocomplete } from "@material-ui/lab";
import { Popper } from "@material-ui/core";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import AWSService from "../../../../services/AWSService";
import CaseService from "../../../../services/CaseService";
import {
  formatToIST,
  getErrorMessage,
  getFileName,
} from "../../../../helpers/functions";
import { splitFileValidationSchema } from "../../../../helpers/validationSchema";
import CustomSelect from "../../NoticeGeneration/component/CustomSelect";
import COLORS from "../../../../assets/Colors";
import { ButtonGroup, BackButton, SubmitButton } from "./styles";
import theme from "../../../../assets/theme";
import CaseLifeCycleStatus from "../../../pages/AssignCaseLifeCycleBulkCase/CaseLifeCycleStatus";
import useUser from "../../../../hooks/useUser";
import AddIcon from "@material-ui/icons/Add";
import {
  AddCircle,
  ChevronIcon,
  StageCard,
  StageHint,
  StageInfo,
  StageName,
  StagePlaceholder,
} from "../../../pages/AssignCaseLifeCycleBulkCase";

export const CustomPopper = (props) => {
  const anchorEl = props.anchorEl;
  const width = anchorEl ? anchorEl.clientWidth : undefined;

  return (
    <Popper
      {...props}
      style={{
        ...props.style,
        width: width || undefined,
        zIndex: 1300,
      }}
      placement="bottom-start"
      modifiers={{
        flip: { enabled: true },
        preventOverflow: { enabled: true, boundariesElement: "scrollParent" },
      }}
    />
  );
};

const UPLOAD_TEMPLATES = [
  {
    name: "contractNo",
    label: "Upload Excel File",
    fileName: "excel",
    accept: ".xlsx",
    types: [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/csv",
    ],
    icon: "📊",
  },
  {
    name: "pdfFile",
    label: "Upload PDF / DOCX Document",
    fileName: "pdf",
    accept: ".pdf, .docx",
    types: [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
    icon: "📄",
  },
];

const VALID_FILE_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/csv",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const CONTRACT_HEADERS = [
  "CONTRACT NUMBER",
  "Contract No",
  "Contract Number",
  "contract no",
];

const WHATSAPP_RECIPIENT_OPTIONS = [
  { label: "Claimant", value: "claimant" },
  { label: "Respondent", value: "respondent" },
  { label: "Neutrals", value: "neutrals" },
  { label: "Case Manager", value: "caseManager" },
  { label: "Co Case Manager", value: "coCaseManager" },
  { label: "All", value: "all" },
];

const INDIVIDUAL_RECIPIENT_VALUES = WHATSAPP_RECIPIENT_OPTIONS.map(
  (o) => o.value,
).filter((v) => v !== "all");

const SplitAndUpload = ({ menuItems }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const { userObject } = useUser();
  const [lifeCycle, setLifeCycle] = useState(false);
  const [status, setStatus] = useState([]);

  async function s3Upload(file, field, setFieldValue, accept, fileName) {
    if (!file) return;

    try {
      setLoader({ state: true, message: `Uploading ${file.name}...` });

      if (!VALID_FILE_TYPES.includes(file?.type)) {
        throw new Error("Invalid File Type");
      }

      if (accept === ".xlsx") {
        const contractNumbers = await readExcelFile(file);
        setFieldValue(field, contractNumbers);
        if (contractNumbers?.length) {
          setFieldValue(fileName, file.name);
          enqueueSnackbar(
            `Excel processed: ${contractNumbers.length} contracts found`,
            {
              variant: "success",
            },
          );
        }
      } else if (accept.includes(".pdf") || accept.includes(".docx")) {
        const payload = { key: file.name };
        const s3Response = await AWSService.getS3URL(payload);

        if (s3Response.url) {
          const res = await AWSService.uploadToS3(s3Response.url, file);
          setFieldValue(field, res);
          enqueueSnackbar("File uploaded successfully", { variant: "success" });
        }
      }
    } catch (err) {
      enqueueSnackbar(err?.message || "Upload failed", { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  const readExcelFile = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      const headerRow = data[0];
      const contractNumberIndex = headerRow.findIndex((header) =>
        CONTRACT_HEADERS.includes(header?.trim()),
      );

      if (contractNumberIndex === -1) {
        throw new Error("Contract number column not found in Excel file");
      }

      const contractNumbers = data
        .slice(1)
        .map((row) => row[contractNumberIndex])
        .filter(Boolean);

      if (contractNumbers.length === 0) {
        throw new Error("No contract numbers found in Excel file");
      }

      return contractNumbers;
    } catch (err) {
      throw err;
    }
  };

  const handleRecipientChange = (
    recipientValue,
    setFieldValue,
    currentSelected = [],
  ) => {
    let nextRecipients;
    const isAllSelected =
      INDIVIDUAL_RECIPIENT_VALUES.length > 0 &&
      INDIVIDUAL_RECIPIENT_VALUES.every((val) => currentSelected.includes(val));

    if (recipientValue === "all") {
      if (isAllSelected) {
        nextRecipients = [];
      } else {
        nextRecipients = [...INDIVIDUAL_RECIPIENT_VALUES];
      }
    } else {
      if (currentSelected.includes(recipientValue)) {
        nextRecipients = currentSelected.filter((v) => v !== recipientValue);
      } else {
        nextRecipients = [...currentSelected, recipientValue];
      }
    }
    setFieldValue("whatsappNotificationRecipient", nextRecipients);
  };

  async function onFormSubmit(values, { resetForm }) {
    try {
      setLoader({ state: true, message: "Processing split and upload..." });

      const sorted = [...status].sort(
        (a, b) => new Date(a.date) - new Date(b.date),
      );
      const selectedStage = sorted[sorted.length - 1];

      const payload = {
        partyId: values.partyId,
        pdfFile: values.pdfFile,
        contractNo: values.contractNo.map(String),
        noticeCategory: values.noticeCategory,
        ...(selectedStage
          ? {
              caseStages: {
                status: selectedStage.value,
                currentUser: userObject?.name || "System",
                stagesDate: formatToIST(selectedStage.date),
                typeOfUpdate: "caseUpdate",
              },
            }
          : {}),
        ...(values.whatsappNotificationRecipient?.length > 0
          ? {
              whatsappNotificationRecipient:
                values.whatsappNotificationRecipient,
            }
          : {}),
      };

      const response = await CaseService.splitUpload(payload);

      if (response?.message) {
        enqueueSnackbar(response.message, { variant: "success" });
      }

      resetForm();
      setStatus([]);
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  return (
    <PageWrapper>
      <Card>
        <Formik
          initialValues={{
            excel: "",
            contractNo: "",
            noticeCategory: "",
            pdfFile: "",
            partyId: "",
            whatsappNotificationRecipient: [],
          }}
          validationSchema={splitFileValidationSchema}
          enableReinitialize
          onSubmit={onFormSubmit}
          validateOnBlur
          validateOnChange
        >
          {({ values, errors, touched, handleSubmit, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <Section>
                <FormGrid>
                  <div>
                    <Label>Notice Category Name</Label>
                    <StyledAutocomplete
                      id="notice-category-autocomplete"
                      size="small"
                      options={menuItems}
                      groupBy={(option) => option.category}
                      getOptionLabel={(option) => option.name}
                      PopperComponent={CustomPopper}
                      renderInput={(params) => (
                        <div ref={params.InputProps.ref}>
                          <CustomInputField
                            {...params}
                            variant="outlined"
                            placeholder="Select notice category..."
                            style={{
                              backgroundColor: "#ffffff",
                              borderRadius: "10px",
                            }}
                          />
                        </div>
                      )}
                      renderGroup={(params) => (
                        <div style={{ padding: "8px 0" }}>
                          <div
                            style={{
                              padding: "10px 16px",
                              fontWeight: "700",
                              fontSize: "11px",
                              color: "#0f766e",
                              textTransform: "uppercase",
                              letterSpacing: "0.8px",
                              backgroundColor: "#f0fdf4",
                              borderRadius: "6px",
                              margin: "4px 8px",
                            }}
                          >
                            {params.group}
                          </div>
                          <div
                            style={{
                              padding: "4px 0",
                              color: COLORS.COLOR_DARK,
                              fontFamily: theme.fonts.primaryFontSemiBold,
                            }}
                          >
                            {params.children}
                          </div>
                        </div>
                      )}
                      noOptionsText={
                        <div
                          style={{
                            color: COLORS.INPUT_LABEL,
                            fontFamily: "openSans-SemiBold, sans-serif",
                            fontSize: "14px",
                            padding: "8px",
                          }}
                        >
                          No document type found
                        </div>
                      }
                      onChange={(event, newValue) =>
                        setFieldValue("noticeCategory", newValue?.name || "")
                      }
                    />
                  </div>

                  <CustomSelect
                    name="partyId"
                    trigger={true}
                    getNoticeTemplates={() => {}}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                    menuItems={menuItems}
                  />
                </FormGrid>
              </Section>

              <Section>
                <FormGrid>
                  {UPLOAD_TEMPLATES.map((upload) => (
                    <div key={upload.name}>
                      <Label>
                        {upload.label}
                        <span className="required-star"> *</span>
                      </Label>
                      <CustomFileUpload
                        error={
                          upload.accept === ".xlsx"
                            ? touched[upload.fileName] &&
                              errors[upload.fileName]
                            : touched[upload.name] && errors[upload.name]
                        }
                        setFile={(file) =>
                          s3Upload(
                            file,
                            upload.name,
                            setFieldValue,
                            upload.accept,
                            upload.fileName,
                          )
                        }
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                          borderRadius: "12px",
                        }}
                        accept={upload.accept}
                        file={{
                          name:
                            upload.fileName === "excel"
                              ? values[upload.fileName]
                              : getFileName(values[upload.name]),
                        }}
                      />
                    </div>
                  ))}
                </FormGrid>
              </Section>

              <Section>
                <Label>WhatsApp Notification With Attachment</Label>
                <RecipientOptions>
                  {WHATSAPP_RECIPIENT_OPTIONS.map((recipient) => {
                    const selected = values.whatsappNotificationRecipient || [];
                    const isAllSelected =
                      INDIVIDUAL_RECIPIENT_VALUES.length > 0 &&
                      INDIVIDUAL_RECIPIENT_VALUES.every((val) =>
                        selected.includes(val),
                      );
                    const isChecked =
                      recipient.value === "all"
                        ? isAllSelected
                        : selected.includes(recipient.value);

                    return (
                      <CheckboxContainer key={recipient.value}>
                        <CustomCheckbox
                          id={`split-wa-${recipient.value}`}
                          inputProps={{
                            id: `split-wa-${recipient.value}`,
                          }}
                          checked={isChecked}
                          onChange={() =>
                            handleRecipientChange(
                              recipient.value,
                              setFieldValue,
                              selected,
                            )
                          }
                        />
                        <CheckBoxLabel htmlFor={`split-wa-${recipient.value}`}>
                          {recipient.label}
                        </CheckBoxLabel>
                      </CheckboxContainer>
                    );
                  })}
                </RecipientOptions>
              </Section>

              <Section>
                <Label>Case Status (Optional)</Label>
                <StageCard
                  filled={status.length > 0}
                  onClick={() => setLifeCycle(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setLifeCycle(true);
                  }}
                >
                  <AddCircle filled={status.length > 0}>
                    <AddIcon style={{ fontSize: 18 }} />
                  </AddCircle>
                  <StageInfo>
                    {status.length ? (
                      <>
                        <StageName>
                          {status.length} stage{status.length > 1 ? "s" : ""}{" "}
                          selected
                        </StageName>
                        <StageHint>Click to modify selections</StageHint>
                      </>
                    ) : (
                      <StagePlaceholder>
                        Click to select stages to apply
                      </StagePlaceholder>
                    )}
                  </StageInfo>
                  <ChevronIcon>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </ChevronIcon>
                </StageCard>
              </Section>

              <ButtonGroup>
                <BackButton
                  type="button"
                  onClick={() => navigate("/dashboard/case-manager")}
                >
                  Back
                </BackButton>
                <SubmitButton onClick={handleSubmit}>
                  Submit
                </SubmitButton>
              </ButtonGroup>
            </form>
          )}
        </Formik>
        <CaseLifeCycleStatus
          lifeCycle={lifeCycle}
          setLifeCycle={setLifeCycle}
          status={status}
          setStatus={setStatus}
        />
      </Card>
    </PageWrapper>
  );
};

export default SplitAndUpload;

export const PageWrapper = styled.div`
  padding: 24px 0px 32px;
  width: 100%;
  box-sizing: border-box;
`;

export const Card = styled.div`
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(41, 52, 97, 0.06);
  padding: 32px;
  border: 1px solid #e5e7eb;

  @media (max-width: 640px) {
    padding: 16px;
  }

  /* Normalise label/input spacing of FormField, CustomSelect and Label so
     every field in a FormGrid row lines up at the same height. */
  & .form-fields {
    padding-top: 0 !important;
  }
  & .form-fields > label {
    display: block;
    margin-bottom: 10px;
    line-height: 1.4;
  }
  & .form-fields > .MuiTextField-root,
  & .form-fields > .MuiAutocomplete-root {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
`;

export const Section = styled.section`
  background: #f9fafb;
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 20px;
  border: 1px solid #e5e7eb;

  @media (max-width: 640px) {
    padding: 16px;
  }
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 10px;
  line-height: 1.4;
  font-size: 12px;
  color: ${COLORS.INPUT_LABEL};
  font-family: openSans-SemiBold, sans-serif;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
  gap: 20px 24px;
  align-items: start;
`;

export const RecipientOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-top: 2px;
`;

export const StyledAutocomplete = styled(Autocomplete)`
  & .MuiAutocomplete-listbox {
    color: #293461;
    font-family: openSans-SemiBold, sans-serif;
    font-size: 14px;
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    max-height: 300px;
    padding: 8px 0;

    & .MuiAutocomplete-option {
      padding: 12px 16px;
      transition: all 0.2s ease;

      &:hover {
        background-color: #f0fdf4;
      }

      &[aria-selected="true"],
      &[data-focus="true"] {
        background-color: #ecfdf5;
      }
    }
  }

  & .MuiAutocomplete-noOptions {
    font-family: openSans-SemiBold, sans-serif;
    color: #6b7280;
    padding: 16px;
    font-size: 14px;
  }
`;
